import { router, usePage } from "@inertiajs/react";
import {
	differenceInCalendarDays,
	format,
	formatDistanceToNow,
} from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { TAG_BADGE_VARIANTS } from "@/lib/therapists";
import { cn } from "@/lib/utils";
import type { TherapistTableRow } from "@/types/admin-portal/therapist";
import type { GlobalPageProps } from "@/types/globals";

/**
 * One badge in the Tags column. `action` is what the dialog offers as a way out; a tag
 * without one leaves the reader with an explanation only, which is the case when they
 * lack the permission to resolve it.
 */
interface TagDescriptor {
	key: string;
	label: string;
	hint: string;
	className: string;
	title: string;
	description: string;
	note?: string;
	action?: { label: string; run: () => void };
}

export interface TherapistTagsCellProps {
	therapist: TherapistTableRow;
	className?: string;
}

/**
 * Renders the Tags column: badges for the things about a therapist record that an admin
 * has to act on. Hovering explains the consequence; clicking opens a dialog that spells
 * it out and offers the way to resolve it.
 */
export function TherapistTagsCell({
	therapist,
	className,
}: TherapistTagsCellProps) {
	const { t } = useTranslation("therapists");
	const { props: globalProps } = usePage<GlobalPageProps>();
	const [activeTag, setActiveTag] = useState<TagDescriptor | null>(null);

	// Lifting a suspension is a real account mutation, and some suspensions are
	// deliberate — keep it with the roles that already own the destructive actions here.
	const canLiftSuspension = useMemo(() => {
		const currentUser = globalProps.auth.currentUser;

		return !!(
			currentUser?.["isSuperAdmin?"] || currentUser?.["isAdminSupervisor?"]
		);
	}, [globalProps.auth.currentUser]);

	const tags = useMemo<TagDescriptor[]>(() => {
		const routes = globalProps.adminPortal.router.adminPortal;
		const flags = therapist.tags;
		const items: TagDescriptor[] = [];

		const goToEdit = () => {
			router.get(`${routes.therapistManagement.index}/${therapist.id}/edit`);
		};

		// Both contract badges read the same date and offer the same way out; only the
		// wording, the colour and the days-left hint differ.
		const contractTag = (
			key: "contract_expiring" | "contract_expired",
			className: string,
		) => {
			// Non-null: the server only raises a contract flag when an end date exists.
			const endDate = new Date(therapist.contractEndDate as string);

			return {
				key,
				label: t(`tags.${key}.label`),
				// Days left, not the window size: "ends in 3 days" and "ends in 28 days"
				// wear the same badge, and only the tooltip can tell them apart.
				hint: t(`tags.${key}.hint`, {
					days: Math.max(differenceInCalendarDays(endDate, new Date()), 0),
				}),
				className,
				title: t(`tags.${key}.title`),
				description: t(`tags.${key}.description`, {
					date: format(endDate, "PP"),
					distance: formatDistanceToNow(endDate, { addSuffix: true }),
				}),
				action: { label: t(`tags.${key}.action`), run: goToEdit },
			};
		};

		if (flags.contractExpiring && therapist.contractEndDate) {
			items.push(
				contractTag("contract_expiring", TAG_BADGE_VARIANTS.notYetLate),
			);
		}

		if (flags.contractExpired && therapist.contractEndDate) {
			items.push(
				contractTag("contract_expired", TAG_BADGE_VARIANTS.alreadyWrong),
			);
		}

		if (flags.noSchedule) {
			items.push({
				key: "no-schedule",
				label: t("tags.no_schedule.label"),
				hint: t("tags.no_schedule.hint"),
				className: TAG_BADGE_VARIANTS.notYetLate,
				title: t("tags.no_schedule.title"),
				description: t("tags.no_schedule.description"),
				action: {
					label: t("tags.no_schedule.action"),
					// Schedules are not part of the therapist form, so Edit cannot fix this.
					// The availability page takes a therapist id and preselects them.
					run: () => {
						router.get(routes.availability.index, {
							therapist: therapist.id,
						});
					},
				},
			});
		}

		if (flags.statusMismatch) {
			items.push({
				key: "status-mismatch",
				label: t("tags.status_mismatch.label"),
				hint: t("tags.status_mismatch.hint"),
				className: TAG_BADGE_VARIANTS.alreadyWrong,
				title: t("tags.status_mismatch.title"),
				description: t("tags.status_mismatch.description", {
					date: therapist.user.suspendAt
						? format(new Date(therapist.user.suspendAt), "PP")
						: "-",
				}),
				note: canLiftSuspension
					? undefined
					: t("tags.status_mismatch.no_permission"),
				action: canLiftSuspension
					? {
							label: t("tags.status_mismatch.action"),
							// Re-saving the therapist form would not clear this: the un-suspend
							// callback only fires when employment_status itself changes. The
							// suspension has to be lifted on the account.
							run: () => {
								router.put(
									routes.therapistManagement.activate,
									deepTransformKeysToSnakeCase({
										user: {
											id: therapist.user.id,
											email: therapist.user.email,
											suspendAt: null,
											suspendEnd: null,
										},
									}),
									{ preserveScroll: true },
								);
							},
						}
					: undefined,
			});
		}

		return items;
	}, [
		therapist,
		t,
		globalProps.adminPortal.router.adminPortal,
		canLiftSuspension,
	]);

	if (!tags.length) {
		return (
			<span className={cn("text-sm text-muted-foreground", className)}>
				{t("list.na")}
			</span>
		);
	}

	return (
		<>
			{/* w-max keeps the badges on one line: a w-full auto-layout table shrinks a
			    column to its min-content width, which would otherwise stack them. */}
			<div className={cn("flex items-center gap-1 flex-wrap w-max", className)}>
				<TooltipProvider>
					{tags.map((tag) => (
						<Tooltip key={tag.key}>
							<TooltipTrigger asChild>
								<button
									type="button"
									// The row itself opens the details panel; a badge click means
									// the badge, not the row.
									onClick={(event) => {
										event.stopPropagation();
										setActiveTag(tag);
									}}
								>
									<Badge
										className={cn(
											"text-[10px] uppercase !px-2 rounded-full shrink-0 text-nowrap cursor-pointer border-transparent",
											tag.className,
										)}
									>
										{tag.label}
									</Badge>
								</button>
							</TooltipTrigger>
							<TooltipContent side="top">
								<span>{tag.hint}</span>
							</TooltipContent>
						</Tooltip>
					))}
				</TooltipProvider>
			</div>

			<AlertDialog
				open={!!activeTag}
				onOpenChange={(open) => {
					if (!open) setActiveTag(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{activeTag?.title}</AlertDialogTitle>
						<AlertDialogDescription>
							{activeTag?.description}
						</AlertDialogDescription>
					</AlertDialogHeader>

					{activeTag?.note && (
						<p className="text-sm text-muted-foreground">{activeTag.note}</p>
					)}

					<AlertDialogFooter>
						<AlertDialogCancel>
							{activeTag?.action ? t("tags.cancel") : t("tags.close")}
						</AlertDialogCancel>
						{activeTag?.action && (
							<AlertDialogAction onClick={activeTag.action.run}>
								{activeTag.action.label}
							</AlertDialogAction>
						)}
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
